import {
  Box,
  Card,
  CollapsableCard,
  Icon,
  Tag,
  Text,
  Touchable,
} from '@factorialco/deprecated-design-system'
import {
  AllocationFragment,
  FinanceValueObjectsAllocationStatusEnum,
} from 'generated/resources/graphql'
import { useToggle } from 'usehooks-ts'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { allocationStatusTag } from 'modules/expenses/lib/budgets/alerts'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

interface Props {
  allocation: AllocationFragment
  currency: string
}

const AllocationStatusCard = ({ allocation, currency }: Props) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const overAllocation = allocation.status === 'over_allocation'
  const [showContent, toggleContent] = useToggle(overAllocation)

  const allocationDescription: Record<FinanceValueObjectsAllocationStatusEnum, string> = {
    within_allocation: i18n.t('budgets.tooltips.expense_within_allocation'),
    allocation_risk: i18n.t('budgets.tooltips.expense_allocation_risk'),
    over_allocation: i18n.t('budgets.tooltips.expense_over_allocation'),
  }

  if (!allocation.amountCents || !allocation.status) return null

  const statusTag = allocationStatusTag(allocation.status)

  return (
    <CollapsableCard
      open={showContent}
      contentBackground="grey000White"
      contentPadding="s8"
      Card={
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
          <Card.Title size="small">
            {i18n.t('budgets.forms.external_resource.budget_allocation.label')}
          </Card.Title>
          <Box gap="s16" flexDirection="row" alignItems="center">
            <Tag type={statusTag.color} inline>
              {statusTag.text}
            </Tag>
            <Touchable onClick={toggleContent}>
              <Icon.Small icon={showContent ? 'chevron-up' : 'chevron-down'} color="grey800" />
            </Touchable>
          </Box>
        </Box>
      }
      Content={
        <Box gap="s8" paddingX="s8">
          <Text.Normal size="200">{allocationDescription[allocation.status]}</Text.Normal>

          <Box flexDirection="row" justifyContent="spaceBetween">
            <Box flexDirection="row" gap="s6" width="full">
              <Text size="200" weight="medium">
                {i18n.t('budgets.detail.tabs.employees.table.allocation')}:
              </Text>
              <Text size="200" weight="medium" color="grey1000">
                {amountWithCurrency(allocation.amountCents, currency, locale)}
              </Text>
            </Box>

            <Box flexDirection="row" gap="s6" width="full">
              <Text size="200" weight="medium">
                {i18n.t('budgets.detail.tabs.employees.table.reported')}:
              </Text>
              <Text size="200" weight="medium" color="grey1000">
                {amountWithCurrency(allocation.reportedAmountCents, currency, locale)}
              </Text>
            </Box>
          </Box>

          <Box flexDirection="row" gap="s6">
            <Text size="200" weight="medium">
              {i18n.t('budgets.detail.tabs.employees.table.available')}:
            </Text>
            <Text size="200" weight="medium" color={overAllocation ? 'radicalRed1200' : 'grey1000'}>
              {amountWithCurrency(Number(allocation.availableAmountCents), currency, locale)}
            </Text>
          </Box>
        </Box>
      }
    />
  )
}

export default AllocationStatusCard
